import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { clampedHorizontalPopoverOffset } from '../app/shell/anchoredMenuPosition';
import { Icon } from './Icon';

/** Shared non-modal help; the trigger never activates its surrounding control. */
export function ContextHelp({
  label,
  text,
  className,
}: {
  readonly className?: string;
  readonly label: string;
  readonly text: string;
}) {
  const id = useId();
  const trigger = useRef<HTMLButtonElement>(null);
  const tooltip = useRef<HTMLSpanElement>(null);
  const dismissal = useRef<number | undefined>(undefined);
  const [open, setOpen] = useState(false);
  const show = () => {
    window.clearTimeout(dismissal.current);
    setOpen(true);
  };
  const leave = () => {
    if (document.activeElement !== trigger.current)
      dismissal.current = window.setTimeout(() => setOpen(false), 150);
  };
  useEffect(() => () => window.clearTimeout(dismissal.current), []);
  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const bounds = trigger.current?.getBoundingClientRect();
      const popup = tooltip.current;
      if (!bounds || !popup) return;
      const size = popup.getBoundingClientRect();
      const gap = 8;
      const margin = 12;
      const desiredLeft = bounds.right - size.width;
      let left =
        desiredLeft +
        clampedHorizontalPopoverOffset(
          { left: desiredLeft },
          size,
          { left: 0, right: window.innerWidth },
          margin,
        );
      const above = bounds.top - size.height - gap;
      const below = bounds.bottom + gap;
      let top = above >= margin ? above : below;
      // A nearby side placement can keep the landing heading clear without covering the card action.
      const dialog = trigger.current?.closest('[data-app-dialog]');
      const headingElement = (
        dialog ?? trigger.current?.closest('.app-start-screen')
      )?.querySelector('h1, h2');
      if (dialog && bounds.right + gap + size.width <= window.innerWidth - margin) {
        // Use nearby free space beside a dialog instead of covering another method card.
        left = bounds.right + gap;
        top = bounds.bottom - size.height;
      }
      const headingRange = document.createRange();
      if (headingElement) headingRange.selectNodeContents(headingElement);
      const heading = headingElement ? headingRange.getBoundingClientRect() : null;
      const headingOverlap = heading
        ? Math.max(0, Math.min(left + size.width, heading.right) - Math.max(left, heading.left))
        : 0;
      if (
        heading &&
        top < heading.bottom &&
        top + size.height > heading.top &&
        headingOverlap > 0
      ) {
        const besideHeading = heading.left - gap - size.width;
        if (besideHeading >= margin && besideHeading + size.width >= bounds.left) {
          // A small horizontal adjustment still leaves the popup immediately above its icon.
          left = besideHeading;
        } else if (bounds.right + gap + size.width <= window.innerWidth - margin) {
          left = bounds.right + gap;
          top = Math.max(heading.bottom + gap, bounds.bottom - size.height);
        }
      }
      popup.style.left = `${left}px`;
      popup.style.top = `${Math.max(margin, Math.min(top, window.innerHeight - size.height - margin))}px`;
      popup.style.visibility = 'visible';
    };
    place();
    const dismiss = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopImmediatePropagation();
      setOpen(false);
    };
    const outside = (event: PointerEvent) => {
      if (
        !trigger.current?.contains(event.target as Node) &&
        !tooltip.current?.contains(event.target as Node)
      )
        setOpen(false);
    };
    window.addEventListener('keydown', dismiss, true);
    window.addEventListener('pointerdown', outside);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('keydown', dismiss, true);
      window.removeEventListener('pointerdown', outside);
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, text]);
  return (
    <span className={className}>
      <button
        type="button"
        className="icon-button context-help-trigger"
        aria-label={`Help: ${label}`}
        aria-describedby={open ? id : undefined}
        aria-expanded={open}
        ref={trigger}
        onMouseEnter={show}
        onMouseLeave={leave}
        onFocus={show}
        onBlur={() => setOpen(false)}
        onClick={(event) => {
          event.stopPropagation();
          setOpen(true);
        }}
      >
        <Icon name="help" />
      </button>
      {open
        ? createPortal(
            <span
              id={id}
              role="tooltip"
              className="context-help-tooltip"
              ref={tooltip}
              style={{ left: 0, top: 0, visibility: 'hidden' }}
              onMouseEnter={show}
              onMouseLeave={leave}
            >
              {text}
            </span>,
            document.body,
          )
        : null}
    </span>
  );
}
